import { inject, Injectable } from '@angular/core';

import { Store } from '@ngxs/store';
import {
	catchError,
	concat,
	first,
	filter,
	map,
	merge,
	Observable,
	shareReplay,
	Subject,
	switchMap,
	take,
	takeUntil,
	takeWhile,
	throwError,
	timeout,
} from 'rxjs';

import { RebasedWebSocketMessage, RebasedWebSocketMessageType } from '@trovata/app/core/models/rebased-web-socket.model';
import { RebasedWebSocketService } from '@trovata/app/core/services/rebased-web-socket.service';
import { AuthState } from '@trovata/app/core/store/state/auth/auth.state';
import {
	AgentStreamEvent,
	AgentChatConfigurationMessage,
	AgentConfigurationResponseMessage,
	AgentConfigurationTextDeltaMessage,
	AgentChatPreviewMessage,
	AgentConfigurationPreviewResponseMessage,
	AgentConfigurationPreviewTextDeltaMessage,
	WebSocketConnectionError,
	buildAgentChatConfigurationRequest,
	buildAgentChatPreviewRequest,
	mintAgentRunId,
} from '@trovata/app/features/ai-agents/models/agents-websocket.model';

/**
 * AgentsWebSocketService - One-shot WebSocket dispatcher for Trovata AI agent chat + preview streams.
 *
 * Sends a single request over the shared `RebasedWebSocketService` and exposes the
 * backend's response as an `Observable<AgentStreamEvent>`. Two entry points, identical
 * protocol (request → processing heartbeats → text deltas → terminal success/error):
 * - `sendConfigurationChat(prompt, sessionId?)` — chat-turn stream in the builder dialog.
 * - `sendPreviewChat(prompt)` — one-off preview-generation stream for the preview tab.
 *
 * Stream events are one of `{ type: 'processing' | 'delta' | 'complete' | 'error' }`.
 * State handlers translate them into `streamingChatContent`/`streamingPreviewContent`
 * updates and finalize into `chatMessages`/`previewContent` on `complete`.
 *
 * Timeout policy: first response within 30s (processing heartbeat normally arrives in 1-2s);
 * subsequent events within 120s of each other. Wire captures show the model can sit silent
 * for over 60s while assembling a large tool payload, and deployed runtimes may not yet emit
 * keep-alives through those silences — the budget covers both until the keep-alives roll out.
 * `abortChatStream()` / `abortPreviewStream()` tear down one entry point's in-flight run;
 * `abortStream()` tears down both (dialog close, session teardown).
 *
 * Mid-stream transport drops are unrecoverable: the backend task's `client.send(...)` calls
 * are keyed to the original API Gateway `connection_id`, and a reconnected client receives a
 * new `connection_id` the running backend task cannot learn about. Lost messages stay lost;
 * the per-event 120s timeout surfaces the failure as a `TimeoutError` and the user must retry.
 */
@Injectable({
	providedIn: 'root',
})
export class AgentsWebSocketService {
	// ==========================================
	// Services
	// ==========================================

	private readonly store: Store = inject(Store);
	private readonly webSocketService: RebasedWebSocketService = inject(RebasedWebSocketService);

	// ==========================================
	// Properties
	// ==========================================

	/**
	 * Abort signal owned by the newest run of each kind — one slot per entry point, replaced by a
	 * fresh Subject every time a run starts.
	 *
	 * Both invariants matter. Per-kind: the two entry points stream independently in the builder
	 * dialog, so stopping chat generation must not tear down a preview that is still running.
	 * Per-run: an abandoned subscription keeps appending its own deltas into the single streaming
	 * slot its successor renders from — and against a backend that echoes no `runId` it consumes
	 * the successor's frames as well. Retiring the previous run before the new one subscribes stops
	 * the two from mixing.
	 *
	 * This is the layer that handles *overlapping* runs. It cannot help once a run has already been
	 * unsubscribed — a timeout does exactly that while the backend keeps streaming. `belongsToRun`
	 * covers that case, and only against a backend that echoes `runId` back: until the echo ships,
	 * a timed-out run's late frames still reach its successor.
	 */
	private chatAbort$: Subject<void> | null = null;
	private previewAbort$: Subject<void> | null = null;

	// ==========================================
	// Public Methods
	// ==========================================

	/**
	 * Opens a chat-turn stream against the configurator agent for the builder dialog.
	 *
	 * @param prompt - The user's message text to send to the configurator.
	 * @param sessionId - Optional existing chat session to continue. Omit to start a new session.
	 * @returns Stream of `AgentStreamEvent`s (processing → deltas → complete | error). May throw
	 *   `WebSocketConnectionError` if the socket is down at send time, or a `TimeoutError` if the
	 *   backend does not emit the first response within 30s or the stream stalls for more than 120s.
	 */
	sendConfigurationChat(prompt: string, sessionId?: string): Observable<AgentStreamEvent> {
		// Snapshot the token at call time. The 5s waitForConnection budget is well below Auth0 refresh
		// cadence, so staleness is not a practical concern — and reading inside switchMap would only
		// obscure the send site for the same result.
		const authorization: string = this.store.selectSnapshot(AuthState.accessToken);
		const runId: string = mintAgentRunId();
		const request: AgentChatConfigurationMessage = buildAgentChatConfigurationRequest(prompt, sessionId, authorization, runId);
		// Claim this run's abort slot before the connection wait, not inside it. `abortChatStream()`
		// reads the slot, so while the slot still held the previous run's signal (or none) Stop,
		// Clear Session and dialog-close were all inert for the length of that wait — the run went on
		// to send, stream, and write into a UI the user had already left. Claiming it at call time
		// makes the run abortable from the moment it is dispatched, and retires its predecessor
		// whether or not this run ever reaches the socket.
		const abort$: Subject<void> = this.retirePreviousRun(this.chatAbort$);
		this.chatAbort$ = abort$;
		return this.waitForConnection().pipe(
			switchMap(() => {
				this.webSocketService.send(request);
				// first: 30s (processing heartbeat arrives within 1-2s normally; 30s is generous)
				// each: 120s — wire-measured model silences exceed 60s on large tool payloads, and a
				// deployed runtime without keep-alives must not trip the timeout during one of them
				return this.buildStream(
					RebasedWebSocketMessageType.aiAgentConfigurationResponse,
					RebasedWebSocketMessageType.aiAgentConfigurationTextDelta,
					{ first: 30_000, each: 120_000 },
					abort$,
					runId
				);
			}),
			takeUntil(abort$)
		);
	}

	/**
	 * Opens a one-off preview stream for the builder dialog's preview tab.
	 *
	 * @param prompt - The prompt text to run against the agent's current instructions.
	 * @param timezone - IANA timezone the run should anchor dates to. An unsaved agent has no
	 *   schedule rule server-side, so the builder form's timezone travels with the request.
	 * @returns Stream of `AgentStreamEvent`s (processing → deltas → complete | error). May throw
	 *   `WebSocketConnectionError` if the socket is down at send time, or a `TimeoutError` if the
	 *   backend does not emit the first response within 30s or the stream stalls for more than 120s.
	 */
	sendPreviewChat(prompt: string, timezone?: string): Observable<AgentStreamEvent> {
		// Snapshot the token at call time — see sendConfigurationChat for rationale.
		const authorization: string = this.store.selectSnapshot(AuthState.accessToken);
		const runId: string = mintAgentRunId();
		const request: AgentChatPreviewMessage = buildAgentChatPreviewRequest(prompt, authorization, timezone, runId);
		// Same call-time claim as the chat stream — see sendConfigurationChat for rationale.
		const abort$: Subject<void> = this.retirePreviousRun(this.previewAbort$);
		this.previewAbort$ = abort$;
		return this.waitForConnection().pipe(
			switchMap(() => {
				this.webSocketService.send(request);
				// Same 120s each-budget as the chat stream — see sendConfigurationChat for rationale.
				return this.buildStream(
					RebasedWebSocketMessageType.aiAgentConfigurationPreviewResponse,
					RebasedWebSocketMessageType.aiAgentConfigurationPreviewTextDelta,
					{ first: 30_000, each: 120_000 },
					abort$,
					runId
				);
			}),
			takeUntil(abort$)
		);
	}

	/**
	 * Tears down the in-flight `sendConfigurationChat` run, leaving any preview run untouched.
	 *
	 * Safe to call when no chat stream is active.
	 */
	abortChatStream(): void {
		this.chatAbort$?.next();
	}

	/**
	 * Tears down the in-flight `sendPreviewChat` run, leaving any chat run untouched.
	 *
	 * Safe to call when no preview stream is active.
	 */
	abortPreviewStream(): void {
		this.previewAbort$?.next();
	}

	/**
	 * Tears down every in-flight stream from this service — chat and preview alike.
	 *
	 * For teardown of the whole surface (builder dialog close, session reset). To stop just one
	 * entry point, use `abortChatStream()` / `abortPreviewStream()` — killing an unrelated stream
	 * strands whatever UI was waiting on it. Safe to call when no stream is active.
	 */
	abortStream(): void {
		this.abortChatStream();
		this.abortPreviewStream();
	}

	// ==========================================
	// Private Methods
	// ==========================================

	/**
	 * Signals the previous run of one kind to stop and mints the new run's abort signal.
	 *
	 * The new Subject is always a fresh instance, so the emission that retires the old run can
	 * never reach the run that replaces it.
	 */
	private retirePreviousRun(previous: Subject<void> | null): Subject<void> {
		previous?.next();
		return new Subject<void>();
	}

	/**
	 * Whether a frame may be consumed by the run identified by `runId`.
	 *
	 * A frame that names a *different* run is discarded. This is the half of the isolation problem
	 * `retirePreviousRun` cannot reach: retiring only helps while both runs are live, but a run that
	 * timed out is already unsubscribed while its backend task keeps streaming. Its late frames —
	 * a terminal success frame above all — would otherwise resolve whichever run the user started
	 * next, publishing the dead run's text and charts under the new prompt.
	 *
	 * An absent or null id means the frame cannot be attributed, so it is accepted: a backend
	 * deployed before the echo shipped labels every frame that way, and dropping them would leave
	 * the feature permanently silent against that deploy.
	 */
	private belongsToRun(frameRunId: string | null | undefined, runId: string): boolean {
		return frameRunId === null || frameRunId === undefined || frameRunId === runId;
	}

	/**
	 * Connection guard — ensures WebSocket is ready and connected before sending.
	 *
	 * After ready$ resolves (initial auth + connection), checks connected$ (BehaviorSubject
	 * replays current value). If connected, resolves instantly. If not, waits up to timeoutMs
	 * for reconnection, then throws WebSocketConnectionError.
	 *
	 * Known limitation 1 (pre-send race): a narrow window exists where connected$ = true but the
	 * socket drops before send() executes. The first-event timeout (30s) catches any lost request.
	 *
	 * Known limitation 2 (mid-stream drop): this guard runs only at the start. If the socket drops
	 * mid-stream, the backend task keeps sending to the now-dead connection_id and messages are lost
	 * permanently (the backend cannot be re-routed to the reconnected client's new connection_id).
	 * The per-event 120s timeout in `buildStream` is the only fallback — the user must retry.
	 */
	private waitForConnection(timeoutMs: number = 5_000): Observable<void> {
		return this.webSocketService.ready$.pipe(
			take(1),
			switchMap(() =>
				this.webSocketService.connected$.pipe(
					first((connected: boolean) => connected),
					timeout(timeoutMs),
					map(() => undefined),
					catchError(() => throwError(() => new WebSocketConnectionError()))
				)
			)
		);
	}

	/**
	 * Turns the raw WebSocket frames for one request/response cycle into an
	 * `Observable<AgentStreamEvent>` the state handler can subscribe to.
	 *
	 * The backend sends:
	 *   - `processing` lifecycle pings while it's working
	 *   - `delta` frames as the response text streams in
	 *   - one final lifecycle frame with `status: 'success'` (or `'error'`)
	 *
	 * We emit those as `'processing' | 'delta' | 'complete' | 'error'` events
	 * and end the stream on the first `'complete'` or `'error'`.
	 *
	 * @param responseType - Message type carrying processing / success / error frames.
	 * @param deltaType - Message type carrying incremental text chunks.
	 * @param timeoutConfig - `first`: max ms before any event arrives; `each`: max ms between events.
	 * @param abort$ - This run's abort signal; emitting on it unsubscribes the run from the socket.
	 * @param runId - This run's correlation id; frames attributed to a different run are discarded.
	 * @returns A stream of `AgentStreamEvent`s that ends on the first terminal event.
	 */
	private buildStream(
		responseType: RebasedWebSocketMessageType,
		deltaType: RebasedWebSocketMessageType,
		timeoutConfig: { first: number; each: number },
		abort$: Observable<void>,
		runId: string
	): Observable<AgentStreamEvent> {
		// Raw frames, filtered off the shared socket by type and then down to this run's own — plus
		// every frame the backend left unattributed, which `belongsToRun` has to accept.
		const lifecycle$: Observable<AgentConfigurationResponseMessage | AgentConfigurationPreviewResponseMessage> = this.webSocketService
			.onMessageType(responseType)
			.pipe(
				map((msg: RebasedWebSocketMessage) => msg as AgentConfigurationResponseMessage | AgentConfigurationPreviewResponseMessage),
				filter((msg: AgentConfigurationResponseMessage | AgentConfigurationPreviewResponseMessage) => this.belongsToRun(msg.payload.runId, runId))
			);

		const deltas$: Observable<AgentConfigurationTextDeltaMessage | AgentConfigurationPreviewTextDeltaMessage> = this.webSocketService
			.onMessageType(deltaType)
			.pipe(
				map((msg: RebasedWebSocketMessage) => msg as AgentConfigurationTextDeltaMessage | AgentConfigurationPreviewTextDeltaMessage),
				filter((msg: AgentConfigurationTextDeltaMessage | AgentConfigurationPreviewTextDeltaMessage) => this.belongsToRun(msg.payload.runId, runId))
			);

		// Fires exactly once — on the first lifecycle frame that signals "done"
		// (status === 'success' or 'error'). Used below both to stop the delta /
		// processing streams AND to produce the final complete/error event.
		//
		// `shareReplay(bufferSize: 1)` buffers that terminal value so a late
		// subscriber (completionEvent$ — see `concat(...)` below) still gets it.
		// Do NOT switch to `refCount: true`: when the terminal frame fires, the
		// takeUntil(done$) subscribers unsubscribe at the same moment, refCount
		// would drop to 0, shareReplay would clear its buffer, and completionEvent$
		// — which only subscribes AFTER deltaEvents$ completes — would see
		// nothing. The stream would hang until the per-event timeout fires.
		const done$: Observable<AgentConfigurationResponseMessage | AgentConfigurationPreviewResponseMessage> = lifecycle$.pipe(
			filter(
				(msg: AgentConfigurationResponseMessage | AgentConfigurationPreviewResponseMessage) =>
					msg.payload.status === 'success' || msg.payload.status === 'error'
			),
			take(1),
			shareReplay({ bufferSize: 1, refCount: false })
		);

		// Heartbeat pings while the backend is working. They're mapped to
		// `'processing'` events so the per-event `timeout(each)` below resets —
		// otherwise a long gap between deltas would trip the timeout early.
		// When the backend labels the frame with the tool it is running, the
		// label rides along so the UI can show live progress.
		const processingEvents$: Observable<AgentStreamEvent> = lifecycle$.pipe(
			filter((msg: AgentConfigurationResponseMessage | AgentConfigurationPreviewResponseMessage) => msg.payload.status === 'processing'),
			takeUntil(done$),
			map(
				(msg: AgentConfigurationResponseMessage | AgentConfigurationPreviewResponseMessage): AgentStreamEvent => ({
					type: 'processing',
					...(msg.payload.message ? { message: msg.payload.message } : {}),
				})
			)
		);

		// Streaming text the UI appends to `streamingChatContent`. Stops the
		// instant the terminal frame arrives so nothing leaks out after 'complete'.
		const deltaEvents$: Observable<AgentStreamEvent> = deltas$.pipe(
			takeUntil(done$),
			map(
				(msg: AgentConfigurationTextDeltaMessage | AgentConfigurationPreviewTextDeltaMessage): AgentStreamEvent => ({
					type: 'delta',
					content: msg.payload.delta,
				})
			)
		);

		// The single terminal event, shaped for the state handler's `switch(event.type)`.
		// A success frame may carry the run's inline charts, and a `message` explaining a
		// degrade (e.g. charts omitted because the payload was oversized); genuine backend
		// failures arrive as `status: 'error'` frames with the reason in `message`.
		const completionEvent$: Observable<AgentStreamEvent> = done$.pipe(
			map(
				(msg: AgentConfigurationResponseMessage | AgentConfigurationPreviewResponseMessage): AgentStreamEvent =>
					msg.payload.status === 'success'
						? {
								type: 'complete',
								...(msg.payload.inlineCharts !== undefined ? { inlineCharts: msg.payload.inlineCharts } : {}),
								...(msg.payload.message ? { message: msg.payload.message } : {}),
							}
						: { type: 'error', message: msg.payload.message ?? 'Stream error' }
			)
		);

		// Final wire-up:
		//   - `merge` runs processing heartbeats concurrently with the delta/completion chain.
		//   - `concat` guarantees all deltas emit BEFORE the terminal event.
		//   - `takeWhile(..., true)` explicitly completes the outer stream after the
		//     terminal event, so `timeout` is cleaned up and subscribers release.
		//   - `takeUntil(abort$)` retires this run — either on an explicit abort or because a newer
		//     run of the same kind replaced it.
		return merge(processingEvents$, concat(deltaEvents$, completionEvent$)).pipe(
			takeWhile((event: AgentStreamEvent) => event.type !== 'complete' && event.type !== 'error', true),
			timeout(timeoutConfig),
			takeUntil(abort$)
		);
	}
}
